import { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Newspaper, Search, ExternalLink, Clock, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MarketNews({ user, onLogout }) {
  const [news, setNews] = useState([]);
  const [filteredNews, setFilteredNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [displayCount, setDisplayCount] = useState(15); // Initial display count

  useEffect(() => {
    fetchNews();
  }, []);

  useEffect(() => {
    filterNews();
  }, [searchQuery, activeCategory, news]);

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/discover/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterNews = () => {
    let filtered = news;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(article => article.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    setFilteredNews(filtered);
    // Reset display count when filters change
    setDisplayCount(15);
  };

  const handleLoadMore = () => {
    setDisplayCount(prev => prev + 10);
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const articleDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - articleDate) / (1000 * 60));
    
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'stocks': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      'economy': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      'policy': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
      'global': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      'crypto': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
              <Newspaper className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Market News</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400 ml-15">Stay updated with the latest financial news</p>
        </div>

        {/* Search Bar */}
        <Card className="glass p-4 mb-6 dark:bg-gray-800">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search news articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 dark:bg-gray-700 dark:text-gray-100"
            />
          </div>
        </Card>

        {/* Category Tabs */}
        <Tabs defaultValue="all" className="w-full mb-6" onValueChange={setActiveCategory}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="stocks">Stocks</TabsTrigger>
            <TabsTrigger value="economy">Economy</TabsTrigger>
            <TabsTrigger value="policy">Policy</TabsTrigger>
            <TabsTrigger value="global">Global</TabsTrigger>
          </TabsList>
        </Tabs>

        {/* News Articles */}
        <div className="space-y-4">
          {filteredNews.length === 0 ? (
            <Card className="glass p-12 dark:bg-gray-800 text-center">
              <Newspaper className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">No articles found matching your criteria</p>
            </Card>
          ) : (
            filteredNews.slice(0, displayCount).map((article, idx) => (
              <Card key={idx} className="glass p-6 dark:bg-gray-800">
                <div className="flex flex-col md:flex-row gap-4">
                  
                  {/* Article Image */}
                  {article.image && (
                    <div className="md:w-48 md:h-32 flex-shrink-0">
                      <img 
                        src={article.image} 
                        alt={article.title}
                        className="w-full h-full object-cover rounded-lg"
                        onError={(e) => { e.target.style.display = 'none' }}
                      />
                    </div>
                  )}
                  
                  {/* Article Content */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-1 rounded-full font-semibold ${getCategoryColor(article.category)}`}>
                          {article.category.toUpperCase()}
                        </span>
                        <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="w-3 h-3 mr-1" />
                          {getTimeAgo(article.published_at)}
                        </div>
                      </div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">
                      {article.title}
                    </h3>
                    
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                      {article.description}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        Source: {article.source}
                      </span>
                      
                      {article.url && article.url !== "#" && (
                        <a 
                          href={article.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                        >
                          Read More
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Load More Button */}
        {filteredNews.length > displayCount && (
          <div className="mt-8 text-center">
            <Button 
              onClick={handleLoadMore}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-all shadow-md hover:shadow-lg"
            >
              Load More Articles
            </Button>
          </div>
        )}

        {/* Footer Note */}
        {filteredNews.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {Math.min(displayCount, filteredNews.length)} of {filteredNews.length} article{filteredNews.length !== 1 ? 's' : ''}
            </p>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}
